import React, { useState } from 'react';
import styled from 'styled-components';
import { database } from '../firebase';
import { ref, push } from 'firebase/database';

const AppContainer = styled.div`
  min-height: 100vh;
  background-color: #ffffff;
  padding: 2rem 1rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const PageBorder = styled.div`
  position: relative;
  border-image-source: url('/frame.png');
  border-image-slice: 150;
  border-image-width: 100px;
  border-image-repeat: stretch;
  border-style: solid;
  padding: calc(5rem + 16px);
  margin: 1rem;
  max-width: 700px;
  width: 100%;
  background-color: #ffffff;

  @media (max-width: 768px) {
    border-image-width: 60px;
    padding: calc(3rem + 16px);
    margin: 0.5rem;
  }
`;

const FormContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 1rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 3rem;
  letter-spacing: 4px;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  color: #333;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9rem;
  letter-spacing: 2px;
  color: #666;
  text-transform: uppercase;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-family: 'Montserrat', sans-serif;
  font-size: 0.85rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #333;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  color: #333;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #999;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SubmitButton = styled.button`
  padding: 1rem 2rem;
  background-color: #333;
  color: white;
  border: none;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin-top: 1rem;

  &:hover {
    background-color: #555;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 2rem;
  background-color: #f0f8f0;
  border: 1px solid #c3e6c3;
  border-radius: 4px;
  margin-top: 2rem;

  h2 {
    color: #2d5f2d;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 0.95rem;
  }
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 4px;
  color: #c33;
  font-size: 0.9rem;
`;

function AddressPage() {
  const [formData, setFormData] = useState({
    name: '',
    street: '',
    street2: '',
    city: '',
    state: '',
    zip: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Please enter your preferred mailing name');
      return;
    }
    if (!formData.street.trim()) {
      setError('Please enter your street address');
      return;
    }
    if (!formData.city.trim()) {
      setError('Please enter your city');
      return;
    }
    if (!formData.state.trim()) {
      setError('Please enter your state');
      return;
    }
    if (!formData.zip.trim()) {
      setError('Please enter your ZIP code');
      return;
    }

    setIsSubmitting(true);

    try {
      const addressRef = ref(database, 'addresses');
      await push(addressRef, {
        ...formData,
        timestamp: new Date().toISOString()
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Error submitting address:', err);
      setError('Failed to submit address. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <AppContainer>
        <PageBorder>
          <FormContainer>
            <Header>
              <Title>Mailing Address</Title>
              <Subtitle>HANA & DAVID'S WEDDING</Subtitle>
            </Header>
            <SuccessMessage>
              <h2>Thank You!</h2>
              <p>Your mailing address has been successfully submitted.</p>
            </SuccessMessage>
          </FormContainer>
        </PageBorder>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <PageBorder>
        <FormContainer>
          <Header>
            <Title>Mailing Address</Title>
            <Subtitle>HANA & DAVID'S WEDDING</Subtitle>
            <Subtitle style={{ marginTop: '0.5rem' }}>OCTOBER 11, 2026</Subtitle>
          </Header>

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="name">Preferred Mailing Name *</Label>
              <Input
                type="text"
                id="name"
                name="name"
                autoComplete="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Mr. and Mrs. Smith"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="street">Street Address *</Label>
              <Input
                type="text"
                id="street"
                name="street"
                autoComplete="address-line1"
                value={formData.street}
                onChange={handleChange}
                placeholder="123 Main Street"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="street2">Address Line 2</Label>
              <Input
                type="text"
                id="street2"
                name="street2"
                autoComplete="address-line2"
                value={formData.street2}
                onChange={handleChange}
                placeholder="Apt, Suite, Unit, etc. (optional)"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="city">City *</Label>
              <Input
                type="text"
                id="city"
                name="city"
                autoComplete="address-level2"
                value={formData.city}
                onChange={handleChange}
                placeholder="New York"
                required
              />
            </FormGroup>

            <Row>
              <FormGroup>
                <Label htmlFor="state">State *</Label>
                <Input
                  type="text"
                  id="state"
                  name="state"
                  autoComplete="address-level1"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="NY"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="zip">ZIP Code *</Label>
                <Input
                  type="text"
                  id="zip"
                  name="zip"
                  autoComplete="postal-code"
                  value={formData.zip}
                  onChange={handleChange}
                  placeholder="10001"
                  required
                />
              </FormGroup>
            </Row>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Address'}
            </SubmitButton>
          </Form>
        </FormContainer>
      </PageBorder>
    </AppContainer>
  );
}

export default AddressPage;
